import { Button } from '@lancar-pos/ui';
import { formatCurrency } from '@lancar-pos/shared';
import { format } from 'date-fns';
import { id } from 'date-fns/locale';
import type { Order } from '../../hooks/useOrders';

interface ReceiptPrintProps {
  order: Order;
  businessName: string;
  businessAddress?: string;
  businessPhone?: string;
  width?: number;
  receiptFooter?: string;
}

export function ReceiptContent({ order, businessName, businessAddress, businessPhone, width, receiptFooter }: ReceiptPrintProps) {
  const receiptWidth = width || 58;
  
  const getPaymentMethodLabel = (method: string) => {
    switch (method) {
      case 'cash':
        return 'TUNAI';
      case 'card':
        return 'KARTU';
      case 'qris':
        return 'QRIS';
      default:
        return method.toUpperCase();
    }
  };

  return (
    <div 
      id="receipt-content" 
      data-order={JSON.stringify(order)} 
      data-receipt-footer={receiptFooter || ''}
      className="p-4 mx-auto" 
      style={{ maxWidth: `${receiptWidth * 4.5}px` }}
    >
      <div className="text-center border-b-2 border-dashed border-gray-300 pb-4 mb-4">
        <h1 className="text-lg font-bold">{businessName}</h1>
        {businessAddress && <p className="text-xs mt-1">{businessAddress}</p>}
        {businessPhone && <p className="text-xs">{businessPhone}</p>}
      </div>

      <div className="mb-4">
        <p className="text-center text-sm font-bold mb-2">{order.orderNumber}</p>
        <p className="text-center text-xs">
          {format(new Date(order.createdAt), 'dd MMM yyyy', { locale: id })}
        </p>
        <p className="text-center text-xs">
          {format(new Date(order.createdAt), 'HH:mm:ss')}
        </p>
      </div>

      <div className="border-t border-b border-dashed border-gray-300 py-4 mb-4">
        {order.items.map((item, index) => (
          <div key={index} className="mb-2">
            <div className="flex justify-between text-xs">
              <span>{item.productName}</span>
              <span className="font-bold">{formatCurrency(item.subtotal)}</span>
            </div>
            <div className="text-[10px] text-gray-600 pl-2">
              {item.variant && <span>{item.variant.name} | </span>}
              {item.quantity} x {formatCurrency(item.price)}
            </div>
            {item.notes && (
              <div className="text-[10px] text-gray-400 italic pl-2">Cat: {item.notes}</div>
            )}
          </div>
        ))}
      </div>

      <div className="mb-4">
        <div className="flex justify-between text-xs">
          <span>Subtotal</span>
          <span>{formatCurrency(order.subtotal)}</span>
        </div>
        {order.discount > 0 && (
          <div className="flex justify-between text-xs">
            <span>Diskon</span>
            <span className="text-green-600">-{formatCurrency(order.discount)}</span>
          </div>
        )}
        {order.tax > 0 && (
          <div className="flex justify-between text-xs">
            <span>Pajak</span>
            <span>{formatCurrency(order.tax)}</span>
          </div>
        )}
        <div className="flex justify-between text-sm font-bold border-t border-dashed border-gray-300 pt-2 mt-2">
          <span>TOTAL</span>
          <span>{formatCurrency(order.total)}</span>
        </div>
      </div>

      <div className="mb-4 bg-gray-50 p-2 rounded">
        <div className="flex justify-between text-xs">
          <span>Pembayaran</span>
          <span>{getPaymentMethodLabel(order.paymentMethod)}</span>
        </div>
        {order.paymentMethod === 'cash' && (
          <>
            <div className="flex justify-between text-xs">
              <span>Uang Diterima</span>
              <span>{formatCurrency(order.paidAmount)}</span>
            </div>
            <div className="flex justify-between text-xs font-bold">
              <span>Kembalian</span>
              <span>{formatCurrency(order.changeAmount)}</span>
            </div>
          </>
        )}
      </div>

      <div className="text-center border-t border-dashed border-gray-300 pt-4">
        {receiptFooter && (
          <p className="text-xs mb-2">{receiptFooter}</p>
        )}
        <p className="text-[10px] text-gray-500">Powered by LancarPOS</p>
      </div>
    </div>
  );
}

function generatePrintHTML(order: Order, businessName: string, businessAddress: string, businessPhone: string, receiptFooter?: string): string {
  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  const getPaymentMethodLabel = (method: string) => {
    switch (method) {
      case 'cash': return 'TUNAI';
      case 'card': return 'KARTU';
      case 'qris': return 'QRIS';
      default: return method.toUpperCase();
    }
  };

  const formatDate = (date: string) => {
    return format(new Date(date), 'dd MMM yyyy', { locale: id });
  };

  const formatTime = (date: string) => {
    return format(new Date(date), 'HH:mm:ss');
  };

  const itemsHTML = order.items.map(item => `
    <div style="margin-bottom: 6px;">
      <div style="display: flex; justify-content: space-between; font-size: 11px;">
        <span>${item.productName}</span>
        <span style="font-weight: bold;">${formatPrice(item.subtotal)}</span>
      </div>
      <div style="font-size: 9px; color: #666; padding-left: 8px;">
        ${item.variant ? `${item.variant.name} | ` : ''}${item.quantity} x ${formatPrice(item.price)}
      </div>
      ${item.notes ? `
        <div style="font-size: 9px; color: #999; font-style: italic; padding-left: 8px;">Cat: ${item.notes}</div>
      ` : ''}
    </div>
  `).join('');

  return `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>Struk - ${order.orderNumber}</title>
  <style>
    @page {
      margin: 0;
      size: auto;
    }
    * {
      box-sizing: border-box;
      margin: 0;
      padding: 0;
    }
    body {
      font-family: 'Courier New', Courier, monospace;
      font-size: 11px;
      line-height: 1.3;
      color: #000;
      width: 58mm;
      margin: 0 auto;
      padding: 3mm;
    }
    .text-center { text-align: center; }
    .text-right { text-align: right; }
    .font-bold { font-weight: bold; }
    .border-b { border-bottom: 1px dashed #000; }
    .border-t { border-top: 1px dashed #000; }
    .border-dashed { border-style: dashed; }
    .py-1 { padding-top: 2px; padding-bottom: 2px; }
    .py-2 { padding-top: 4px; padding-bottom: 4px; }
    .py-4 { padding-top: 8px; padding-bottom: 8px; }
    .mb-2 { margin-bottom: 4px; }
    .mb-4 { margin-bottom: 8px; }
    .mt-1 { margin-top: 2px; }
    .mt-2 { margin-top: 4px; }
    .mt-4 { margin-top: 8px; }
    table { width: 100%; border-collapse: collapse; }
    td { padding: 2px; }
    .bg-gray-50 { background: #f9fafb; }
    .p-2 { padding: 4px; }
    .rounded { border-radius: 2px; }
    @media print {
      body { width: 58mm; }
    }
  </style>
</head>
<body>
  <div style="text-align: center; border-bottom: 2px dashed #000; padding-bottom: 8px; margin-bottom: 8px;">
    <div style="font-size: 14px; font-weight: bold;">${businessName}</div>
    ${businessAddress ? `<div style="font-size: 9px; margin-top: 2px;">${businessAddress}</div>` : ''}
    ${businessPhone ? `<div style="font-size: 9px;">${businessPhone}</div>` : ''}
  </div>

  <div style="margin-bottom: 8px;">
    <div style="text-align: center; font-weight: bold; margin-bottom: 2px;">${order.orderNumber}</div>
    <div style="text-align: center; font-size: 9px;">
      ${formatDate(order.createdAt)} ${formatTime(order.createdAt)}
    </div>
  </div>

  <div style="border-top: 1px dashed #000; border-bottom: 1px dashed #000; padding: 8px 0; margin-bottom: 8px;">
    ${itemsHTML}
  </div>

  <div style="margin-bottom: 8px;">
    <div style="display: flex; justify-content: space-between; font-size: 10px;">
      <span>Subtotal</span>
      <span>${formatPrice(order.subtotal)}</span>
    </div>
    ${order.discount > 0 ? `
    <div style="display: flex; justify-content: space-between; font-size: 10px;">
      <span>Diskon</span>
      <span style="color: #16a34a;">-${formatPrice(order.discount)}</span>
    </div>
    ` : ''}
    ${order.tax > 0 ? `
    <div style="display: flex; justify-content: space-between; font-size: 10px;">
      <span>Pajak</span>
      <span>${formatPrice(order.tax)}</span>
    </div>
    ` : ''}
    <div style="display: flex; justify-content: space-between; font-size: 12px; font-weight: bold; border-top: 1px dashed #000; padding-top: 4px; margin-top: 4px;">
      <span>TOTAL</span>
      <span>${formatPrice(order.total)}</span>
    </div>
  </div>

  <div style="margin-bottom: 8px; background: #f9fafb; padding: 4px; border-radius: 2px;">
    <div style="display: flex; justify-content: space-between; font-size: 10px;">
      <span>Pembayaran</span>
      <span>${getPaymentMethodLabel(order.paymentMethod)}</span>
    </div>
    ${order.paymentMethod === 'cash' ? `
    <div style="display: flex; justify-content: space-between; font-size: 10px;">
      <span>Uang Diterima</span>
      <span>${formatPrice(order.paidAmount)}</span>
    </div>
    <div style="display: flex; justify-content: space-between; font-size: 10px; font-weight: bold;">
      <span>Kembalian</span>
      <span>${formatPrice(order.changeAmount)}</span>
    </div>
    ` : ''}
  </div>

  <div style="text-align: center; border-top: 1px dashed #000; padding-top: 8px;">
    ${receiptFooter ? `<div style="font-size: 10px; margin-bottom: 4px;">${receiptFooter}</div>` : ''}
    <div style="font-size: 8px; color: #666;">Powered by LancarPOS</div>
  </div>
</body>
</html>
  `;
}

export function usePrintReceipt() {
  const handlePrint = (footerArg?: unknown) => {
    const receiptFooter = typeof footerArg === 'string' ? footerArg : undefined;
    
    const printContent = document.getElementById('receipt-content');
    if (!printContent) {
      console.error('Receipt content not found');
      return;
    }

    const businessName = printContent.querySelector('h1')?.textContent || 'LancarPOS';
    const paragraphs = printContent.querySelectorAll('p');
    const businessAddress = paragraphs[0]?.textContent || '';
    const businessPhone = paragraphs[1]?.textContent || '';

    let order: Order | null = null;
    const orderDataAttr = printContent.getAttribute('data-order');
    if (orderDataAttr) {
      try {
        order = JSON.parse(orderDataAttr);
      } catch (e) {
        console.error('Failed to parse order data:', e);
      }
    }

    if (!order) {
      console.error('Order data not found');
      return;
    }

    const footer = receiptFooter || printContent.getAttribute('data-receipt-footer') || '';
    
    const printHTML = generatePrintHTML(order, businessName, businessAddress, businessPhone, footer);
    
    const printWindow = window.open('', '_blank', 'width=350,height=600');
    if (!printWindow) {
      alert('Please allow popups to print');
      return;
    }

    printWindow.document.write(printHTML);
    printWindow.document.close();
    printWindow.focus();

    setTimeout(() => {
      printWindow.print();
    }, 500);
  };

  return { handlePrint };
}

export function PrintButton({ onClick }: { onClick: () => void }) {
  return (
    <Button variant="outline" size="sm" onClick={onClick} className="print:hidden">
      Cetak Struk
    </Button>
  );
}
